import { queryOptions } from '@tanstack/react-query'
import type { UsersFilterValues } from 'context/UsersViewContext'
import { deleteRequest, getRequest, IDENTITY_PREFIX, postRequest, putRequest } from 'global/helpers/requests'
import { type PaginatedResults } from 'global/types/pagination'
import type {
  BulkCreateUsersDialogFormValues,
  CreateOrEditUserFormValues,
  GrantRoleFormValues,
  NotificationsFormValues,
  RevokeRoleFormValues,
  StoreLanguageCommand,
  TemperaturePreferenceFormValues,
  UserData,
  UserPreferencesData,
} from './UserManager.types'

export const USERS_QUERY_KEY = 'users'
export const USER_PREFERENCES_QUERY_KEY = 'user-preferences'

export const getFilteredUsers = async (
  page: number,
  filters: UsersFilterValues,
): Promise<PaginatedResults<UserData>> => {
  const search = new URLSearchParams(
    Object.entries(filters).flatMap(([key, values]) => (values as string[]).map(value => [key, value])),
  )
  search.append('page', page.toString())
  return await getRequest<PaginatedResults<UserData>>(`users?${search.toString()}`, {}, IDENTITY_PREFIX)
}

export const createUser = async (data: CreateOrEditUserFormValues): Promise<void> =>
  await postRequest<void>('users', { body: data }, IDENTITY_PREFIX)

export const editUser = async (cid: string, data: CreateOrEditUserFormValues): Promise<void> => {
  const processedData = {
    ...data,
    userCid: cid,
  }
  return await putRequest<void>(`users/${cid}`, { body: processedData }, IDENTITY_PREFIX)
}

export const deleteUser = async (cid: string): Promise<void> =>
  await deleteRequest<void>(`users/${cid}`, {}, IDENTITY_PREFIX)

export const uploadBulkUsers = async (data: BulkCreateUsersDialogFormValues): Promise<string[]> => {
  const formData = new FormData()
  formData.append('form', data.csv)
  return await postRequest<string[]>('users/bulk', { body: formData }, IDENTITY_PREFIX)
}

export const grantRole = async (cid: string, data: GrantRoleFormValues): Promise<void> =>
  await postRequest<void>(`users/${cid}/grant`, {
    body: {
      ...data,
      userCid: cid,
    },
  }, IDENTITY_PREFIX)

export const revokeRole = async (cid: string, data: RevokeRoleFormValues): Promise<void> =>
  await postRequest<void>(`users/${cid}/revoke`, {
    body: {
      ...data,
      userCid: cid,
    },
  }, IDENTITY_PREFIX)

export const storeLanguage = async (data: StoreLanguageCommand): Promise<void> =>
  await postRequest<void>('user/language', {
    body: {
      language: data.language.toUpperCase().replace('-', '_'),
    },
  }, IDENTITY_PREFIX)

export const getUserPreferences = async (): Promise<UserPreferencesData> =>
  await getRequest<UserPreferencesData>('user/preferences', {}, IDENTITY_PREFIX)

export const userPreferencesQueryOptions = () => queryOptions({
  queryKey: [USER_PREFERENCES_QUERY_KEY],
  queryFn: getUserPreferences,
})

export const saveUserPreferences = async (data: UserPreferencesData): Promise<void> =>
  await postRequest<void>('user/preferences', { body: data }, IDENTITY_PREFIX)

export const updateNotificationPreferences = async (data: NotificationsFormValues): Promise<void> =>
  await postRequest<void>('user/preferences', { body: data }, IDENTITY_PREFIX)

export const updateTemperaturePreferences = async (data: TemperaturePreferenceFormValues): Promise<void> =>
  await postRequest<void>('user/preferences', { body: data }, IDENTITY_PREFIX)
